import { MessageTypes } from "@tmi/types/enums/message-types";
import { createDefaultLogger, LogLevels } from "@/types/console-logger";
import {
    CACHE_SCHEMA_VERSION, CONTENT_COOLDOWN_KEYS, CONTENT_KEY_PATHS, CONTENT_STORES, DATABASE_NAME, STATE_STORE, STORE_NAMES, type StoreName
} from "./cache-schema";

const logger = createDefaultLogger( LogLevels.DEBUG );

// Literal names, not `new.target.name`: the production build minifies class names, and these names are what
// logs and incident reports carry (F-M9).
export class CacheError extends Error {
    override name: string = 'CacheError';
    constructor( message: string, options?: { cause?: unknown } ) {
        super( message, options );
    }
}
export class CacheOpenError extends CacheError {
    override name: string = 'CacheOpenError';
}
export class CacheClosedError extends CacheError {
    override name: string = 'CacheClosedError';
}

/**
 * Every cache failure a consumer sees is a CacheError carrying `cause`; a CacheError subclass passes through
 * unchanged so the quiet-stop `instanceof CacheClosedError` checks still match. `operation` names the failing
 * call in the message (`articles.put`, `resetCachedContent`, …). Shared by the stores, the maintenance entry
 * points, `cache.transaction` and the in-memory fake, so all four fail with the same type and wording.
 */
export async function wrapCacheError< R >( operation: string, work: () => R | Promise< R > ): Promise< R > {
    try {
        return await work();
    } catch ( err: unknown ) {
        if ( err instanceof CacheError ) {
            throw err;
        }
        const detail = ( err as { name?: string; message?: string } | null );
        throw new CacheError( `[ cache ] ${ operation } failed: ${ detail?.name ?? 'Error' }${ detail?.message ? ` — ${ detail.message }` : '' }`, { cause: err } );
    }
}

/** How long a caller waits on an open or delete sitting in `blocked` (another context has not closed) before it is told. */
export const BLOCKED_TIMEOUT_MS: number = 10_000;
/**
 * How long a caller waits on an open or delete that has produced NO event at all — not even `blocked` — before it is
 * told. Armed when the wait starts and disarmed by any answer, `blocked` included (from there BLOCKED_TIMEOUT_MS
 * bounds the wait). WebKit drops a `versionchange` aimed at a worker connection whose open success has not reached
 * the worker thread yet, never acks it, and so never fires `blocked` either: the request queues silently, forever
 * (tmi-nn4). Longer than BLOCKED_TIMEOUT_MS so an ordinary frozen tab still reports as blocked.
 */
export const OPEN_TIMEOUT_MS: number = 15_000;
/**
 * sessionStorage key recording that THIS tab has already done its one VersionError recovery. It never expires
 * while the tab lives: a time window let an older-build tab evict the newer tabs again every time it lapsed.
 */
export const RECOVERY_BREAKER_KEY = 'tmi-cache:recovered-at' as const;

export function requestToPromise< T >( request: IDBRequest< T > ): Promise< T > {
    return new Promise( ( resolve, reject ) => {
        request.onsuccess = () => resolve( request.result );
        request.onerror = () => reject( request.error ?? new DOMException( 'IndexedDB request failed', 'UnknownError' ) );
    } );
}

export function transactionDone( tx: IDBTransaction ): Promise< void > {
    return new Promise( ( resolve, reject ) => {
        tx.oncomplete = () => resolve();
        // A failed request's `error` event bubbles here BEFORE the transaction aborts and sets `tx.error`, so the
        // real cause (e.g. ConstraintError) lives on the event's target request (F-M2).
        tx.onerror = ( event: Event ) => reject(
            ( event?.target as IDBRequest | null )?.error ?? tx.error ?? new DOMException( 'IndexedDB transaction failed', 'UnknownError' )
        );
        tx.onabort = () => reject( tx.error ?? new DOMException( 'IndexedDB transaction aborted', 'AbortError' ) );
    } );
}

/**
 * Runs inside the versionchange transaction. Deletes stores this build no longer declares (only an older
 * database can reach here, so an undeclared store is always a retired one) and content stores whose keyPath
 * differs from CONTENT_KEY_PATHS (a keyPath cannot be altered in place; before v7 every store was out-of-line),
 * then creates missing stores; when upgrading an existing database, empties every content store and drops the
 * cooldown keys in the same atomic unit. `cache-owner` is kept on purpose: the same user's re-sync must not look
 * like a change of hands. `sync_state` is never recreated: its rows survive, and its values are primitives.
 */
export function applyUpgrade( db: IDBDatabase, tx: IDBTransaction, oldVersion: number ): void {
    const declared: readonly string[] = STORE_NAMES;
    for ( const name of Array.from( db.objectStoreNames ) ) {
        if ( !declared.includes( name ) ) {
            db.deleteObjectStore( name );
        }
    }
    for ( const name of CONTENT_STORES ) {
        if ( db.objectStoreNames.contains( name ) && tx.objectStore( name ).keyPath !== CONTENT_KEY_PATHS[ name ] ) {
            db.deleteObjectStore( name );
        }
    }
    for ( const name of CONTENT_STORES ) {
        if ( !db.objectStoreNames.contains( name ) ) {
            db.createObjectStore( name, { keyPath: CONTENT_KEY_PATHS[ name ] } );
        }
    }
    if ( !db.objectStoreNames.contains( STATE_STORE ) ) {
        db.createObjectStore( STATE_STORE );
    }
    if ( oldVersion > 0 ) {
        for ( const name of CONTENT_STORES ) {
            tx.objectStore( name ).clear();
        }
        const state = tx.objectStore( STATE_STORE );
        for ( const key of CONTENT_COOLDOWN_KEYS ) {
            state.delete( key );
        }
    }
}

// ---------------------------------------------------------------------------------------------------
// Eviction of THIS context when another context upgrades the database (F1).
// ---------------------------------------------------------------------------------------------------

export interface StaleContext {
    isWindow: boolean;
    reload(): void;
    postMessage( message: unknown ): void;
    closeWorker(): void;
}

const reloadWindowNow = (): void => window.location.reload();
let windowReload: () => void = reloadWindowNow;

/**
 * Replaces the window's reload after an eviction. The app installs one that waits out an in-flight sign-in
 * (stores/Application.ts); this module never imports app code. The connection is closed either way — only
 * the navigation waits.
 */
export function setStaleWindowReload( reload: () => void ): void {
    windowReload = reload;
}

function defaultStaleContext(): StaleContext {
    const isWindow = typeof window !== 'undefined';
    const worker = self as unknown as DedicatedWorkerGlobalScope;
    return {
        isWindow,
        reload: () => windowReload(),
        postMessage: message => worker.postMessage( message ),
        closeWorker: () => worker.close()
    };
}

let reloadTriggered = false;

/** Window: reload. Worker: tell the window and stop. Idempotent within one context lifetime (R49). */
export function handleStaleConnection( context: StaleContext = defaultStaleContext() ): void {
    if ( reloadTriggered ) {
        return;
    }
    reloadTriggered = true;
    if ( context.isWindow ) {
        logger.warn( '[ cache ] a newer build upgraded the database — reloading this window' );
        context.reload();
    } else {
        logger.warn( '[ cache ] a newer build upgraded the database — asking the window to reload' );
        context.postMessage( { eventType: MessageTypes.RELOAD_REQUIRED } );
        context.closeWorker();
    }
}

let staleHandler: () => void = () => handleStaleConnection();

/** Tests replace the eviction behavior. */
export function onStaleConnection( handler: () => void ): void {
    staleHandler = handler;
}

// ---------------------------------------------------------------------------------------------------
// Incident reporting (F-I2). The logger is a no-op in production, so the three failure modes that change
// what a user sees are ALSO handed to an injected reporter. The main thread wires it to the app's
// client-error channel (plugins/CachePlugin.ts); this module never imports app code.
// ---------------------------------------------------------------------------------------------------

export type CacheIncident = {
    kind: 'version-error-recovery' | 'recovery-refused' | 'blocked-timeout' | 'open-timeout';
    database: string;
    error?: unknown;
};

let incidentReporter: ( incident: CacheIncident ) => void = () => { /* default: the logger calls alone */ };

export function onCacheIncident( reporter: ( incident: CacheIncident ) => void ): void {
    incidentReporter = reporter;
}

/** Never throws: a broken reporter must not break the open/delete it is reporting on. */
function reportIncident( incident: CacheIncident ): void {
    try {
        incidentReporter( incident );
    } catch ( err: unknown ) {
        logger.warn( `[ cache ] the incident reporter threw while reporting "${ incident.kind }": ${ ( err as Error )?.message ?? err }` );
    }
}

// ---------------------------------------------------------------------------------------------------
// Connection state. `reloading` is set by versionchange and cleared ONLY by closeDatabase() (tests):
// once a newer build owns the database, nothing in this context may reopen it (R39).
// ---------------------------------------------------------------------------------------------------

/**
 * The one in-flight `indexedDB.open`, shared by every caller until it settles. A blocked request is never
 * abandoned: the timeout rejects the callers waiting at that moment, the request stays queued, later callers
 * join it with a fresh wait, and its eventual success becomes the connection (tmi-cya #1).
 */
/** Which request the attempt is waiting on. VersionError recovery is open → recovery delete → reopen, one attempt. */
type OpenStage = 'open' | 'recovery-delete' | 'reopen';

/** What `openOnce` tells its attempt while it runs: blocked events, and each recovery step it moves to. */
interface OpenProgress {
    blocked(): void;
    stage( stage: OpenStage ): void;
}

interface OpenAttempt {
    result: Promise< IDBDatabase >;
    /** Resolves the first time any of the attempt's requests (open, recovery delete, reopen) reports `blocked`. */
    blocked: Promise< void >;
    /** Names the request in timeout messages, so a stuck recovery delete is not reported as a silent open (tmi-4ux). */
    stage: OpenStage;
    /** One blocked-timeout incident per request, however many callers time out on it. */
    timeoutReported: boolean;
    /** One open-timeout incident per request, however many callers give up on its silence (tmi-nn4). */
    noResponseReported: boolean;
}

let connection: IDBDatabase | null = null;
let attempt: OpenAttempt | null = null;
let reloading = false;

/** Only the window deletes-and-recreates on VersionError; the worker fails its pass and retries later (F36). */
let recoverFromVersionErrorHere: boolean = typeof window !== 'undefined';
export function setVersionErrorRecovery( enabled: boolean ): void {
    recoverFromVersionErrorHere = enabled;
}

// Both breaker helpers run inside `request.onerror`: a throw there would leave the open settled-but-unresolved
// forever. Storage that is unavailable (SecurityError, blocked site data) reads as "not yet recovered" and the
// write is skipped — recovery stays bounded by `reloading` and the per-context open memo (F-M4).
function recoveredInThisTab(): boolean {
    try {
        if ( typeof sessionStorage === 'undefined' ) {
            return false;
        }
        return Number( sessionStorage.getItem( RECOVERY_BREAKER_KEY ) ) > 0;
    } catch {
        return false;
    }
}

function recordRecovery(): void {
    try {
        if ( typeof sessionStorage !== 'undefined' ) {
            sessionStorage.setItem( RECOVERY_BREAKER_KEY, String( Date.now() ) );
        }
    } catch {
        /* storage unavailable: skip the breaker write */
    }
}

export function deleteDatabase( name: string, blockedTimeoutMs: number = BLOCKED_TIMEOUT_MS ): Promise< void > {
    return new Promise( ( resolve, reject ) => {
        let settled = false;
        let timer: ReturnType< typeof setTimeout > | undefined;
        const request = indexedDB.deleteDatabase( name );
        const watchdog = setTimeout( () => {
            settled = true;
            const error = new CacheOpenError( `[ cache ] delete of "${ name }" got no response for ${ OPEN_TIMEOUT_MS } ms — the browser delivered neither success, error nor blocked` );
            if ( DATABASE_NAME === name ) {   // same rule as a blocked delete (tmi-cya #5)
                reportIncident( { kind: 'open-timeout', database: name, error } );
            }
            reject( error );
        }, OPEN_TIMEOUT_MS );
        request.onsuccess = () => { clearTimeout( timer ); clearTimeout( watchdog ); if ( settled ) return; settled = true; resolve(); };
        request.onerror = () => { clearTimeout( timer ); clearTimeout( watchdog ); if ( settled ) return; settled = true; reject( request.error ?? new DOMException( `deleteDatabase("${ name }") failed`, 'UnknownError' ) ); };
        request.onblocked = () => {
            clearTimeout( watchdog );
            logger.warn( `[ cache ] delete of "${ name }" is blocked until another context closes it` );
            timer ??= setTimeout( () => {
                settled = true;
                const error = new CacheOpenError( `[ cache ] delete of "${ name }" stayed blocked for ${ blockedTimeoutMs } ms` );
                // A legacy delete blocked by an old-bundle tab is expected during a rollout and retried every
                // boot; only the live database's blocked delete changes what a user sees (tmi-cya #5).
                if ( DATABASE_NAME === name ) {
                    reportIncident( { kind: 'blocked-timeout', database: name, error } );
                }
                reject( error );
            }, blockedTimeoutMs );
        };
    } );
}

/**
 * The recovery delete, run INSIDE the open attempt. Like the open it has no timers of its own: the attempt's callers
 * bound their wait and report one incident per attempt, and the request lives on so a late success still lets the
 * recovery finish. A timed delete here reported a second incident and abandoned the recovery, which the
 * once-per-tab breaker then refused to retry (tmi-4ux).
 */
function recoveryDelete( progress: OpenProgress ): Promise< void > {
    return new Promise( ( resolve, reject ) => {
        progress.stage( 'recovery-delete' );
        const request = indexedDB.deleteDatabase( DATABASE_NAME );
        request.onsuccess = () => resolve();
        request.onerror = () => reject( new CacheOpenError( `[ cache ] the recovery delete of "${ DATABASE_NAME }" failed: ${ request.error?.name ?? 'unknown error' }`, { cause: request.error } ) );
        request.onblocked = () => {
            logger.warn( `[ cache ] the recovery delete of "${ DATABASE_NAME }" is blocked until another context closes it` );
            progress.blocked();
        };
    } );
}

const STAGE_LABEL: Record< OpenStage, string > = {
    'open': `open of "${ DATABASE_NAME }" v${ CACHE_SCHEMA_VERSION }`,
    'recovery-delete': `recovery delete of "${ DATABASE_NAME }" (this build is older than the database)`,
    'reopen': `reopen of "${ DATABASE_NAME }" v${ CACHE_SCHEMA_VERSION } after the recovery delete`
};

/** Issues the open. No timeout here: the request lives until it succeeds or errors; callers bound their own wait. */
function openOnce( recoverFromVersionError: boolean, progress: OpenProgress ): Promise< IDBDatabase > {
    return new Promise( ( resolve, reject ) => {
        let request: IDBOpenDBRequest;
        try {
            request = indexedDB.open( DATABASE_NAME, CACHE_SCHEMA_VERSION );
        } catch ( err: unknown ) {
            // e.g. SecurityError where IndexedDB is disabled: keep the CacheError contract (F-M3).
            reject( new CacheOpenError( `[ cache ] could not open "${ DATABASE_NAME }" at v${ CACHE_SCHEMA_VERSION }: ${ ( err as { name?: string } | null )?.name ?? 'unknown error' }`, { cause: err } ) );
            return;
        }

        request.onupgradeneeded = ( event ) => {
            applyUpgrade( request.result, request.transaction!, event.oldVersion );
        };

        request.onblocked = () => {
            logger.warn( `[ cache ] upgrade to v${ CACHE_SCHEMA_VERSION } is waiting for another context to close its connection` );
            progress.blocked();
        };

        request.onerror = () => {
            const error = request.error;
            if ( 'VersionError' === error?.name ) {
                if ( !recoverFromVersionError ) {
                    reject( new CacheOpenError( `[ cache ] "${ DATABASE_NAME }" is newer than this build (v${ CACHE_SCHEMA_VERSION }); the window recreates it — this pass is skipped`, { cause: error } ) );
                    return;
                }
                if ( reloading || recoveredInThisTab() ) {
                    reportIncident( { kind: 'recovery-refused', database: DATABASE_NAME, error } );
                    reject( new CacheOpenError( `[ cache ] "${ DATABASE_NAME }" is newer than this build (v${ CACHE_SCHEMA_VERSION }) and this tab has already recovered once — refusing to delete it again; close this tab or update the app`, { cause: error } ) );
                    return;
                }
                // This build is OLDER than the database: a rollback, or a stale precache. One deliberate reset per tab.
                logger.error( `[ cache ] this build expects schema v${ CACHE_SCHEMA_VERSION } but "${ DATABASE_NAME }" is newer — deleting and recreating it` );
                reportIncident( { kind: 'version-error-recovery', database: DATABASE_NAME, error } );
                recordRecovery();
                recoveryDelete( progress )
                    .then( () => { progress.stage( 'reopen' ); return openOnce( false, progress ); } )
                    .then( resolve, reject );
                return;
            }
            reject( new CacheOpenError( `[ cache ] could not open "${ DATABASE_NAME }" at v${ CACHE_SCHEMA_VERSION }: ${ error?.name ?? 'unknown error' }`, { cause: error } ) );
        };

        request.onsuccess = () => {
            const db = request.result;
            db.onversionchange = () => {
                db.close();
                connection = null;
                attempt = null;
                reloading = true;
                staleHandler();
            };
            db.onclose = () => {
                connection = null;   // the browser force-closed us (storage cleared, etc.); reopen lazily
                attempt = null;
            };
            resolve( db );
        };
    } );
}

/**
 * One caller's wait on the shared attempt: bounded by OPEN_TIMEOUT_MS while the request is silent, then by
 * BLOCKED_TIMEOUT_MS counted from when the caller saw it blocked. Either timeout rejects this caller only.
 */
function awaitAttempt( current: OpenAttempt ): Promise< IDBDatabase > {
    return new Promise( ( resolve, reject ) => {
        let settled = false;
        let timer: ReturnType< typeof setTimeout > | undefined;
        const watchdog = setTimeout( () => {
            if ( settled ) return;
            settled = true;
            const error = new CacheOpenError( `[ cache ] ${ STAGE_LABEL[ current.stage ] } got no response for ${ OPEN_TIMEOUT_MS } ms — the browser delivered neither success, error nor blocked; the request stays queued and the next call waits on it` );
            if ( !current.noResponseReported ) {
                current.noResponseReported = true;
                reportIncident( { kind: 'open-timeout', database: DATABASE_NAME, error } );
            }
            reject( error );
        }, OPEN_TIMEOUT_MS );
        void current.blocked.then( () => {
            clearTimeout( watchdog );
            if ( settled ) return;
            timer = setTimeout( () => {
                settled = true;
                const error = new CacheOpenError( `[ cache ] ${ STAGE_LABEL[ current.stage ] } stayed blocked for ${ BLOCKED_TIMEOUT_MS } ms — another tab is holding the database; the request stays queued and the next call waits on it` );
                if ( !current.timeoutReported ) {
                    current.timeoutReported = true;
                    reportIncident( { kind: 'blocked-timeout', database: DATABASE_NAME, error } );
                }
                reject( error );
            }, BLOCKED_TIMEOUT_MS );
        } );
        current.result.then(
            db => { clearTimeout( timer ); clearTimeout( watchdog ); if ( settled ) return; settled = true; resolve( db ); },
            err => { clearTimeout( timer ); clearTimeout( watchdog ); if ( settled ) return; settled = true; reject( err ); }
        );
    } );
}

/**
 * Memoized on success; a failed open is retried by the next call; a blocked open is shared, never re-issued.
 * Refuses to reopen after a versionchange.
 */
export function openDatabase(): Promise< IDBDatabase > {
    if ( reloading ) {
        return Promise.reject( new CacheClosedError( '[ cache ] the connection was closed because a newer build upgraded the database; a reload is in flight' ) );
    }
    if ( connection ) {
        return Promise.resolve( connection );
    }
    if ( !attempt ) {
        let markBlocked!: () => void;
        const blocked = new Promise< void >( resolve => { markBlocked = resolve; } );
        const current: OpenAttempt = { blocked, stage: 'open', timeoutReported: false, noResponseReported: false, result: Promise.resolve( null as unknown as IDBDatabase ) };
        const progress: OpenProgress = { blocked: markBlocked, stage: stage => { current.stage = stage; } };
        current.result = openOnce( recoverFromVersionErrorHere, progress ).then(
            db => {
                // closeDatabase() (tests) superseded this attempt while it was queued: do not resurrect it.
                if ( attempt !== current ) {
                    db.close();
                    throw new CacheClosedError( '[ cache ] a superseded open completed after the cache was closed' );
                }
                attempt = null;
                connection = db;
                return db;
            },
            ( err: unknown ) => {
                if ( attempt === current ) {
                    attempt = null;
                }
                throw err;
            }
        );
        // Every caller may already have timed out: the settled result is observed per caller, never unhandled here.
        current.result.catch( () => { /* observed by awaitAttempt */ } );
        attempt = current;
    }
    return awaitAttempt( attempt );
}

/** Tests only. */
export function closeDatabase(): void {
    connection?.close();
    connection = null;
    attempt = null;
    reloading = false;
    reloadTriggered = false;
    windowReload = reloadWindowNow;
}

/**
 * One transaction over `stores`; resolves with `work`'s result once the transaction has COMMITTED. `work`
 * may only await IndexedDB requests issued on `tx`. A throw inside `work` aborts the transaction (F34/R40).
 */
export async function runTransaction< R >(
    stores: readonly StoreName[],
    mode: IDBTransactionMode,
    work: ( tx: IDBTransaction ) => R | Promise< R >
): Promise< R > {
    const db = await openDatabase();            // rejects with CacheClosedError once `reloading` is set
    // A versionchange can land between that resolution and this call, leaving `db` closed; `db.transaction()`
    // then throws InvalidStateError synchronously. Surface it as CacheClosedError so the stores' quiet-stop
    // guard sees a legitimate reload rather than a raw DOMException (R3a). ONLY `reloading` means a reload is
    // coming: a browser force-close (`onclose`: storage cleared, disk error) also leaves `db !== connection`,
    // but nothing will reload the page, so that is a plain CacheError and the next call reopens (F-I1).
    let tx: IDBTransaction;
    try {
        tx = db.transaction( stores as string[], mode );
    } catch ( err: unknown ) {
        if ( reloading ) {
            throw new CacheClosedError( '[ cache ] the connection closed before the transaction could start; a reload is in flight', { cause: err } );
        }
        throw new CacheError( `[ cache ] could not start a ${ mode } transaction over [ ${ stores.join( ', ' ) } ]`, { cause: err } );
    }
    const done = transactionDone( tx );
    done.catch( () => { /* observed below; prevents an unhandled rejection when work() throws first */ } );
    let result: R;
    try {
        result = await work( tx );
    } catch ( err: unknown ) {
        try { tx.abort(); } catch { /* already finished or aborting */ }
        throw err;
    }
    await done;
    return result;
}
